import React, { useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useForm, FieldErrors, useWatch } from "react-hook-form";
import CommonForm from "Components/Common/CommonForm";
import { useMasterPeriodService } from "modules/Library/Period/api/usePeriodService";
import { designFields } from "../helper/design.constant";

interface AddEditDesignProps {
  isOpen: boolean;
  toggle: () => void;
  existingData: any | null;
  onSave: (record: any) => void;
  isLoading: boolean;
}

const AddEditDesign: React.FC<AddEditDesignProps> = ({
  isOpen,
  toggle,
  existingData,
  onSave,
  isLoading,
}) => {
  // Fetch master periods for the dropdown
  const { SelectAllQuery: masterPeriodSelectAll } = useMasterPeriodService(
    "",
    undefined,
    1,
    10,
  );

  const masterPeriodData: any[] =
    (masterPeriodSelectAll as any)?.data?.data ?? [];
  const periodOptions = masterPeriodData.map((item: any) => ({
    label: String(item.period_number),
    value: String(item.period_number),
  }));



  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      designtitle: "",
      sequencetype: "",
      numberofsequences: 0,
      numberoftreatments: 0,
      blocksize: 0,
      period: "",
    },
    mode: "onChange",
  });

  const watchedSequenceType = useWatch({ control, name: "sequencetype" });

  useEffect(() => {
    if (watchedSequenceType && typeof watchedSequenceType === "string") {
      const sequences = watchedSequenceType
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);

      const sequenceCount = sequences.length;

      // Extract complete treatment tokens (e.g., T1, R1, A, B, R2) instead of single characters
      const treatmentSet = new Set<string>();
      sequences.forEach((seq: string) => {
        const tokens = seq.match(/[A-Za-z]+\d*/g) || [];
        tokens.forEach((token: string) => treatmentSet.add(token));
      });

      const uniqueTreatments = treatmentSet.size;

      setValue("numberofsequences", sequenceCount);
      setValue("numberoftreatments", uniqueTreatments);
      setValue("blocksize", sequenceCount * 2);
    } else {
      setValue("numberofsequences", 0);
      setValue("numberoftreatments", 0);
      setValue("blocksize", 0);
    }
  }, [watchedSequenceType, setValue]);

  useEffect(() => {
    if (existingData) {
      reset({
        designtitle: existingData.designtitle || "",
        sequencetype: existingData.sequencetype || "",
        numberofsequences: existingData.numberofsequences || 0,
        numberoftreatments: existingData.numberoftreatments || 0,
        blocksize: (existingData.numberofsequences || 0) * 2,
        period: existingData.period || "",
      });
    } else {
      reset({
        designtitle: "",
        sequencetype: "",
        numberofsequences: 0,
        numberoftreatments: 0,
        blocksize: 0,
        period: "",
      });
    }
  }, [existingData, reset, isOpen]);

  const onSubmit = (values: any): void => {
    const finalValues = {
      ...values,
      numberofsequences: getValues("numberofsequences"),
      numberoftreatments: getValues("numberoftreatments"),
    };
    onSave(finalValues);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="p-3">
        {existingData ? "Edit Design" : "Add Design"}
      </ModalHeader>

      <ModalBody>
        <form id="designForm" onSubmit={handleSubmit(onSubmit)}>
          <CommonForm
            fields={designFields(periodOptions)}
            control={control}
            errors={errors as FieldErrors}
            columns={2}
            setValue={setValue}
            getValues={getValues}
          />
        </form>
      </ModalBody>

      <ModalFooter>
        <Button
          outline
          color="dark"
          size="sm"
          onClick={toggle}
          className="me-2"
        >
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          type="submit"
          form="designForm"
          disabled={isLoading}
          outline
        >
          {isLoading && <Spinner size="sm" className="me-2" />}
          {existingData ? "Update" : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddEditDesign;
